import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getOffices, deleteOffice } from "services/hrms.api";
import OfficeTable from "views/hrms/offices/OfficeTable";
import { LayoutGrid, List as ListIcon } from "lucide-react";
import Modal from "components/ui/Modal";
import Loading from "components/common/Loading";

export default function OfficeList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [variant, setVariant] = useState("grid");
  const [err, setErr] = useState("");

  const navigate = useNavigate();

  // delete confirm
  const [confirm, setConfirm] = useState({ open: false, id: null, name: "" });
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      setErr("");
      setLoading(true);

      const { data } = await getOffices();
      const raw = data?.items ?? data ?? [];
      const items = Array.isArray(raw) ? raw : [];

      const normalized = items.map((o) => ({
        ...o,
        id: o.id || o._id?.toString?.() || o._id, // stable id for UI
      }));

      setRows(normalized);
    } catch (e) {
      console.error(e);
      setErr(
        e?.response?.data?.message || e?.message || "Failed to load offices"
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const empty = useMemo(() => (rows?.length ?? 0) === 0, [rows]);
  const hasOffice = !empty; // ✅ single-office logic

  const askDelete = (id, name) =>
    setConfirm({ open: true, id, name: name || "" });

  const closeDelete = () => setConfirm({ open: false, id: null, name: "" });

  const confirmDelete = async () => {
    if (!confirm.id) return;
    try {
      setDeleting(true);
      await deleteOffice(confirm.id);
      closeDelete();
      await load(); // ✅ reload so Add button reappears if list is now empty
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || e?.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  if (loading && rows.length === 0) {
    return (
      <div className="p-4 md:p-6">
        <Loading variant="dots" fullscreen />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold dark:text-white">
            Manage Office
          </h2>
          {err ? (
            <div className="text-rose-600 dark:text-rose-300 mt-2 text-sm">
              {err}
            </div>
          ) : null}
        </div>

        <div className="flex w-full items-center gap-2 md:w-auto">
          {/* <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search offices…"
            className="dark:bg-transparent h-10 w-full rounded-lg border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700 md:w-72"
          /> */}

          <button
            onClick={() => setVariant("grid")}
            className={`flex h-10 w-10 items-center justify-center rounded-md border dark:border-white/10 ${
              variant === "grid" ? "bg-gray-100 dark:bg-white/10" : ""
            }`}
            title="Grid view"
            type="button"
          >
            <LayoutGrid className="h-5 w-5 dark:text-white" />
          </button>

          <button
            onClick={() => setVariant("table")}
            className={`flex h-10 w-10 items-center justify-center rounded-md border dark:border-white/10 ${
              variant === "table" ? "bg-gray-100 dark:bg-white/10" : ""
            }`}
            title="List view"
            type="button"
          >
            <ListIcon className="h-5 w-5 dark:text-white" />
          </button>

          {/* ✅ Show Add Office ONLY when empty */}
          {!hasOffice && (
            <button
              onClick={() => navigate("new")}
              className="ml-1 flex h-10 items-center gap-2 rounded-lg bg-gradient-to-r from-[#ef145d] to-[#e3955e] px-4 text-sm text-white hover:opacity-95"
              type="button"
            >
              + Add Office
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <TableSkeleton />
      ) : empty ? (
        <EmptyState
          title="No offices added yet"
          subtitle="Create your first office to enable attendance radius & geo-fencing."
          actionLabel="Add Office"
          onAction={() => navigate("new")}
        />
      ) : (
        <OfficeTable
          rows={rows}
          variant={variant}
          onView={(id) => navigate(`${id}`)}
          onEdit={(id) => navigate(`${id}/edit`)}
          onDelete={(id, name) => askDelete(id, name)}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        open={confirm.open}
        onClose={deleting ? undefined : closeDelete}
        title="Delete office?"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-white/80">
            Are you sure you want to delete{" "}
            <span className="font-medium">{confirm.name || "this office"}</span>
            ? This action cannot be undone.
          </p>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              disabled={deleting}
              onClick={closeDelete}
              className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-60 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
            >
              Cancel
            </button>

            <button
              onClick={confirmDelete}
              disabled={deleting}
              className="focus:ring-rose-500 rounded-lg bg-gradient-to-r from-[#ef145d] to-[#e3955e] px-5 py-2 text-sm text-white hover:opacity-95 focus:outline-none focus:ring-2 disabled:opacity-60"
              type="button"
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 dark:border-white/10">
      <div className="animate-pulse space-y-0">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-12 border-b border-gray-100 bg-gray-50/70 last:border-0 dark:border-white/10 dark:bg-white/[0.03]"
          />
        ))}
      </div>
    </div>
  );
}

function EmptyState({ title, subtitle, actionLabel, onAction }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-gray-300 p-8 text-center dark:border-white/10">
      <div className="text-sm font-semibold dark:text-white">{title}</div>
      <div className="text-xs text-gray-500 dark:text-white/70">{subtitle}</div>
      {onAction && (
        <button
          onClick={onAction}
          className="mt-2 rounded-lg bg-brand-500 px-4 py-2 text-xs text-white hover:opacity-95"
          type="button"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}
